/**
 * AIAssistantWidget — floating chat bubble + panel powered by /api/ai/chat.
 *
 * - Mode auto: "portal" when user is logged in, "public" otherwise.
 * - Session id persisted in localStorage so the LLM keeps multi-turn context.
 * - Markdown-light rendering (line breaks + bold via simple text-formatting).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Sparkles, Send, X, Loader2, MessageSquare, Phone } from "lucide-react";

const SESSION_KEY = "lwt.ai.session";
const HISTORY_KEY = "lwt.ai.history";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

function formatText(text) {
  // Light formatting: preserve line breaks, bold **x**, links
  const html = (text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(https?:\/\/[^\s)]+)/g, '<a href="$1" target="_blank" rel="noreferrer" class="underline text-wine-600">$1</a>')
    .replace(/\n/g, "<br/>");
  return { __html: html };
}

export default function AIAssistantWidget() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
      // One-time cleanup: drop stale "Agent IA non configuré" messages that
      // were persisted before the backend dynamic-key fix.
      return stored.filter((m) => !((m?.error || m?.role === "assistant") &&
        typeof m?.content === "string" &&
        m.content.toLowerCase().includes("agent ia non configuré")));
    } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem(SESSION_KEY) || uid());
  const scrollRef = useRef(null);

  // Persist session id once
  useEffect(() => { localStorage.setItem(SESSION_KEY, sessionRef.current); }, []);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Persist history (cap to last 30 messages)
  useEffect(() => {
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-30))); } catch { /* ignore quota */ }
  }, [messages]);

  const mode = user ? "portal" : "public";

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const userMsg = { role: "user", content: text, at: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", { mode, session_id: sessionRef.current, message: text, lang: (lang || "fr").toLowerCase() });
      setMessages((m) => [...m, { role: "assistant", content: r.data?.reply || "(réponse vide)", at: Date.now() }]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const friendly = typeof detail === "string" && detail
        ? detail
        : (e.response?.status === 429 ? S.rateLimited : S.unavailable);
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${friendly}`, at: Date.now(), error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    sessionRef.current = uid();
    localStorage.setItem(SESSION_KEY, sessionRef.current);
    setMessages([]);
  };

  const suggestions = (() => {
    const L = (lang || "fr").toLowerCase();
    if (L === "en") return mode === "portal"
      ? ["How to boost my score?", "How to publish a news item?", "How to enable matchmaking?"]
      : ["What are the upcoming events?", "Who are the members in Languedoc?", "How to join La WineTech?"];
    if (L === "es") return mode === "portal"
      ? ["¿Cómo mejorar mi puntuación?", "¿Cómo publicar una noticia?", "¿Cómo activar el matchmaking?"]
      : ["¿Cuáles son los próximos eventos?", "¿Quiénes son los miembros en Languedoc?", "¿Cómo unirse a La WineTech?"];
    if (L === "pt") return mode === "portal"
      ? ["Como aumentar minha pontuação?", "Como publicar uma notícia?", "Como ativar o matchmaking?"]
      : ["Quais são os próximos eventos?", "Quem são os membros em Languedoc?", "Como aderir à La WineTech?"];
    return mode === "portal"
      ? ["Comment augmenter mon score ?", "Comment publier une actualité ?", "Comment activer le matchmaking ?"]
      : ["Quels sont les prochains événements ?", "Qui sont les membres au Languedoc ?", "Comment rejoindre La WineTech ?"];
  })();

  // Sprint 92h — Localized UI strings for FR/EN/ES/PT
  const L = (lang || "fr").toLowerCase();
  const S = {
    fr: { modePortal: "Aide portail · ", modePublic: "Public · ", reset: "Reset", tooltipReset: "Nouvelle conversation",
          intro: "Bonjour ! Je suis l'assistant IA de <strong>La WineTech</strong>. Posez-moi une question sur",
          topicPortal: "votre portail membre", topicPublic: "la communauté, les événements ou les membres",
          typing: "écrit…", placeholder: "Écrivez votre question…",
          fallbackTitle: "Pas la bonne réponse ?", fallbackHint: "Contactez", fallbackWho: "Audrey", fallbackLangs: "directement (FR/EN/ES/PT)",
          waLabel: "WhatsApp", callLabel: "Appeler",
          rateLimited: "L'assistant est très sollicité, réessayez dans quelques secondes.",
          unavailable: "L'assistant est temporairement indisponible. Réessayez dans un instant." },
    en: { modePortal: "Portal help · ", modePublic: "Public · ", reset: "Reset", tooltipReset: "New conversation",
          intro: "Hi! I'm the AI assistant of <strong>La WineTech</strong>. Ask me anything about",
          topicPortal: "your member portal", topicPublic: "the community, events or members",
          typing: "typing…", placeholder: "Type your question…",
          fallbackTitle: "Not the right answer?", fallbackHint: "Contact", fallbackWho: "Audrey", fallbackLangs: "directly (FR/EN/ES/PT)",
          waLabel: "WhatsApp", callLabel: "Call",
          rateLimited: "The assistant is busy, please retry in a few seconds.",
          unavailable: "The assistant is temporarily unavailable. Please try again shortly." },
    es: { modePortal: "Ayuda portal · ", modePublic: "Público · ", reset: "Reiniciar", tooltipReset: "Nueva conversación",
          intro: "¡Hola! Soy el asistente IA de <strong>La WineTech</strong>. Pregúntame sobre",
          topicPortal: "tu portal de miembro", topicPublic: "la comunidad, los eventos o los miembros",
          typing: "escribiendo…", placeholder: "Escribe tu pregunta…",
          fallbackTitle: "¿No es la respuesta correcta?", fallbackHint: "Contacta a", fallbackWho: "Audrey", fallbackLangs: "directamente (FR/EN/ES/PT)",
          waLabel: "WhatsApp", callLabel: "Llamar",
          rateLimited: "El asistente está muy solicitado, reinténtalo en unos segundos.",
          unavailable: "El asistente no está disponible por el momento. Inténtalo de nuevo pronto." },
    pt: { modePortal: "Ajuda portal · ", modePublic: "Público · ", reset: "Reiniciar", tooltipReset: "Nova conversa",
          intro: "Olá! Sou o assistente de IA da <strong>La WineTech</strong>. Pergunte-me sobre",
          topicPortal: "o seu portal de membro", topicPublic: "a comunidade, os eventos ou os membros",
          typing: "a escrever…", placeholder: "Escreva a sua pergunta…",
          fallbackTitle: "Não é a resposta certa?", fallbackHint: "Contacte a", fallbackWho: "Audrey", fallbackLangs: "diretamente (FR/EN/ES/PT)",
          waLabel: "WhatsApp", callLabel: "Ligar",
          rateLimited: "O assistente está muito solicitado, tente novamente em alguns segundos.",
          unavailable: "O assistente está temporariamente indisponível. Tente novamente em breve." },
  }[["fr","en","es","pt"].includes(L) ? L : "fr"];

  return (
    <>
      {/* Floating bubble */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Assistant IA La WineTech"
        className="fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-wine-600 text-white shadow-2xl ring-4 ring-white/40 transition-transform hover:scale-105 hover:bg-wine-700"
        data-testid="ai-bubble"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
        {!open && messages.length > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-[10px] font-bold text-neutral-900">
            {messages.filter((m) => m.role === "assistant").length}
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-[60] flex h-[560px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-2xl" data-testid="ai-panel">
          {/* Header */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-wine-600 to-rose-500 px-4 py-3 text-white">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold">Assistant La WineTech</div>
              <div className="text-[10px] opacity-90">
                {mode === "portal" ? S.modePortal : S.modePublic}Claude Sonnet 4.6
              </div>
            </div>
            <button onClick={reset} className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold hover:bg-white/20" title={S.tooltipReset} data-testid="ai-reset">
              {S.reset}
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-neutral-50 p-4" data-testid="ai-messages">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="rounded-2xl bg-white p-3 text-sm text-neutral-700 shadow-sm">
                  <MessageSquare className="mr-1.5 inline h-3.5 w-3.5 text-wine-600" />
                  <span dangerouslySetInnerHTML={{ __html: `${S.intro} ${mode === "portal" ? S.topicPortal : S.topicPublic}.` }} />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(s)}
                      className="rounded-full border border-wine-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-wine-700 hover:bg-wine-50"
                      data-testid={`ai-suggest-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.role === "user" ? "bg-wine-600 text-white" : "bg-white text-neutral-800"}`} dangerouslySetInnerHTML={formatText(m.content)} />
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="ai-busy">
                <div className="inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm text-neutral-500 shadow-sm">
                  <Loader2 className="h-3 w-3 animate-spin" />{S.typing}
                </div>
              </div>
            )}
          </div>

          {/* Sprint 92f — Always-visible WhatsApp fallback to Audrey */}
          <div className="border-t border-neutral-100 bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-2">
            <div className="flex items-center gap-2 text-[11px] text-neutral-700">
              <span className="flex-1 leading-snug">
                <strong>{S.fallbackTitle}</strong> {S.fallbackHint} <strong>{S.fallbackWho}</strong> {S.fallbackLangs}
              </span>
              <a
                href="https://wa.me/33660235665?text=Bonjour%20Audrey%2C%20je%20viens%20du%20site%20La%20WineTech%20et%20j%27ai%20une%20question%20%3A%20"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold text-white shadow-sm hover:bg-emerald-600"
                data-testid="ai-fallback-whatsapp"
                title="WhatsApp Audrey"
              >
                <MessageSquare className="h-3 w-3" /> {S.waLabel}
              </a>
              <a
                href="tel:+33660235665"
                className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-neutral-800 shadow-sm ring-1 ring-neutral-200 hover:bg-neutral-50"
                data-testid="ai-fallback-call"
                title="Appeler Audrey"
              >
                <Phone className="h-3 w-3" /> {S.callLabel}
              </a>
            </div>
          </div>

          {/* Input */}
          <div className="flex items-end gap-2 border-t border-neutral-100 bg-white p-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder={S.placeholder}
              className="flex-1 resize-none rounded-2xl border border-neutral-200 px-3 py-2 text-sm focus:border-wine-500 focus:outline-none"
              data-testid="ai-input"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-wine-600 text-white hover:bg-wine-700 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="ai-send"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
